import classNames from "classnames";
import { useSelector } from "react-redux";
import { lazy, useEffect, useMemo, useRef, useState, Suspense } from "react";
import { useMediaQuery } from "react-responsive";
const Slider = lazy(() => import("react-slick"));

import Player from "../Player";
import LazyImage from "../LazyImage";
import {
  getMaxFileHeight,
  isAudioFile,
  isImageFile,
  isMediaFile,
} from "@helpers/files";
import { ReactComponent as SliderArrowIcon } from "@assets/ic-slider-arrow.svg";
import { getCurrentSrc } from "@helpers/posts";
import { noop } from "@helpers/common";
import { XL } from "@const/breakpoints";

import styles from "../ViewPost/index.module.scss";

const CustomPrevArrow = (props) => {
  const { onClick } = props;
  return (
    <SliderArrowIcon
      {...props}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
    />
  );
};

const CustomNextArrow = (props) => {
  const { onClick } = props;
  return (
    <SliderArrowIcon
      {...props}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
    />
  );
};

const ImageFile = ({
  onOpenModal,
  contentItem,
  wrapperItemClassName,
  maxFileHeight,
  isPostCreating,
  showOriginalFiles,
}) => {
  const src = useMemo(() => {
    return isPostCreating
      ? contentItem?.blob
      : getCurrentSrc(contentItem.file, showOriginalFiles);
  }, [isPostCreating, contentItem, showOriginalFiles]);
  return (
    <LazyImage
      src={src}
      height={maxFileHeight}
      imageClassName="mh-100"
      wrapperClassName={wrapperItemClassName}
      onClick={() => onOpenModal(false)}
    />
  );
};

const CustomPagingDots = ({
  currentSlide,
  slideCount,
  pagingDots,
  setCurrentSlide,
  goToSlide,
}) => {
  const [range, setRange] = useState({
    start: 0,
    end: Math.min(slideCount, pagingDots),
  });

  const lastItemIndex = slideCount - 1;

  useEffect(() => {
    let start = range.start;
    let end = range.end;
    if (currentSlide === 0) {
      start = 0;
      end = Math.min(slideCount, pagingDots);
    } else if (currentSlide === lastItemIndex) {
      end = slideCount;
      start = Math.max(0, slideCount - pagingDots);
    } else {
      if (currentSlide <= range.start && range.start > 0) {
        start = Math.max(0, currentSlide - Math.floor(pagingDots / 2));
        end = Math.min(slideCount, start + pagingDots);
      } else if (currentSlide >= range.end - 1 && range.end < slideCount) {
        end = Math.min(
          slideCount,
          currentSlide + Math.floor(pagingDots / 2) + 1,
        );
        start = Math.max(0, end - pagingDots);
      }
    }

    setRange({ start, end });
  }, [currentSlide, pagingDots, slideCount]);
  const dots = [];

  // Create the dots
  for (let i = range.start; i < range.end; i++) {
    dots.push(
      <li
        key={i}
        className={i === currentSlide ? "slick-active" : "slick-no-active"}
        onClick={() => {
          goToSlide(i);
          setCurrentSlide(i);
        }}
      >
        <button />
      </li>,
    );
  }

  // Add the ellipsis dots if needed
  if (range.start > 0) {
    dots.unshift(
      <li
        key="start-ellipsis"
        className="start-more"
        onClick={() => {
          goToSlide(0);
          setCurrentSlide(0);
        }}
      >
        <button />
      </li>,
    );
  }
  if (range.end < slideCount) {
    dots.push(
      <li
        key="end-ellipsis"
        className="end-more"
        onClick={() => {
          goToSlide(lastItemIndex);
          setCurrentSlide(lastItemIndex);
        }}
      >
        <button />
      </li>,
    );
  }

  return <ul className="custom-dots translate-middle-x start-50">{dots}</ul>;
};

const MediaFile = ({
  setDraggable,
  contentItem,
  previewImage,
  wrapperItemClassName,
  isPostCreating,
  maxFileHeight,
  hideFullscreen,
}) => {
  const isAudio = isAudioFile(contentItem.file);
  const mediaSource = useMemo(
    () => [
      {
        type: contentItem?.file?.contentType,
        src: contentItem?.file?.link,
      },
    ],
    [contentItem?.file?.contentType, contentItem?.file?.link],
  );

  const sources = isPostCreating ? contentItem?.blob : mediaSource;

  const poster = isPostCreating
    ? contentItem?.file?.poster?.poster || contentItem?.poster
    : getCurrentSrc(previewImage);

  return (
    <Player
      poster={poster}
      isAudio={isAudio}
      sources={sources}
      className={classNames(styles.player, wrapperItemClassName)}
      style={{ height: maxFileHeight }}
      preload="none"
      hideFullscreen={hideFullscreen}
      setDraggable={setDraggable}
    />
  );
};

MediaFile.displayName = "MediaFile";

const ViewFiles = ({
  content = [],
  onOpenModal = noop,
  wrapperItemClassName,
  isPostCreating,
  showOriginalFiles,
  defaultCurrentSlide = 0,
  hideFullscreen,
}) => {
  const sliderRef = useRef(null);
  const [draggable, setDraggable] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(defaultCurrentSlide);

  useEffect(() => {
    if (defaultCurrentSlide) {
      setCurrentSlide(defaultCurrentSlide);
      sliderRef?.current?.slickGoTo(currentSlide);
    }
  }, [defaultCurrentSlide]);

  const maxFileHeight = getMaxFileHeight(content);

  const isDesktop = useMediaQuery({
    query: `(min-width: ${XL})`,
  });

  const { ref: currentPlayer } = useSelector(
    ({ playerReducer }) => playerReducer,
  );

  const pauseVideo = () => {
    if (!currentPlayer?.player_ || !currentPlayer?.pause) return;
    currentPlayer?.pause?.();
  };

  if (!content.length) {
    return null;
  }

  return (
    <Suspense fallback="Loading...">
      <Slider
        speed={500}
        ref={sliderRef}
        className="mb-2"
        infinite={false}
        slidesToShow={1}
        slidesToScroll={1}
        dots={content.length > 1}
        draggable={draggable}
        arrows={isDesktop && content.length > 1}
        dotsClass="custom-dots translate-middle-x start-50"
        beforeChange={(oldIndex, newIndex) => {
          pauseVideo();
          sliderRef?.current?.slickGoTo(newIndex);
          setCurrentSlide(newIndex);
        }}
        prevArrow={<CustomPrevArrow />}
        nextArrow={<CustomNextArrow />}
        appendDots={(dots) => (
          <CustomPagingDots
            currentSlide={currentSlide}
            slideCount={dots.length}
            pagingDots={10}
            setCurrentSlide={setCurrentSlide}
            goToSlide={(index) => sliderRef?.current?.slickGoTo(index)}
          />
        )}
      >
        {content?.map((contentItem) => {
          return (
            <div
              key={contentItem.id}
              className="m-auto h-100 d-xl-block d-flex align-items-center justify-content-center position-relative"
            >
              {isImageFile(contentItem.file) && (
                <ImageFile
                  onOpenModal={onOpenModal}
                  contentItem={contentItem}
                  wrapperItemClassName={wrapperItemClassName}
                  maxFileHeight={maxFileHeight}
                  isPostCreating={isPostCreating}
                  showOriginalFiles={showOriginalFiles}
                />
              )}
              {isMediaFile(contentItem.file) && (
                <MediaFile
                  setDraggable={setDraggable}
                  contentItem={contentItem}
                  wrapperItemClassName={wrapperItemClassName}
                  previewImage={contentItem.previewImage}
                  isPostCreating={isPostCreating}
                  maxFileHeight={maxFileHeight}
                  hideFullscreen={hideFullscreen}
                />
              )}
            </div>
          );
        })}
      </Slider>
    </Suspense>
  );
};

export default ViewFiles;
